
import { List, Map } from 'immutable';

import { filterOperations, replaceOperation } from './operations';
import { updateProductVariationsErrors } from './errors';
import { areComplete, setIsComplete } from './isComplete';
import { editVariations } from '../edit/variations';
import { newVariations } from '../new/product';
import { editDetails } from '../edit/details';
import { editTags } from '../edit/tags';
import { getSize } from '../iterable/getSize';
import { convert } from './convert';

import { CATEGORY, NEW_TAGS, TAGS, VARIATIONS } from '../../constants/attributes';
import { OPERATIONS, VALUE } from '../../constants/product';
import { MAX_NUMBER_OF_TAGS } from '../../constants/validations';
import { EMPTY, VELA } from '../../constants';
import { ETSY } from '../../constants/channels';
import { PROFILE } from '../../constants/profiles';
import { FORM } from '../../constants/forms';

export function clearProfile({ channel, fromVelaForm, shopId, state, type }) {
  function reduceChannelsForTags(productsByShopId, shopIds, shopsChannel) {
    const newTags = state.getIn([VELA, TAGS, NEW_TAGS]);

    function reduceShopIds(result, productShopId) {
      function updateProduct(source) {
        let product = source
          .deleteIn([TAGS, NEW_TAGS])
          .deleteIn([TAGS, 'currentProfile'])
          .setIn([TAGS, VALUE], EMPTY.ORDERED_MAP)
          .set('operations',
            source
              .get('operations', EMPTY.LIST)
              .filter(filterOperations(OPERATIONS[shopsChannel].TAGS.CHANGE_TO, false))
          );

        if (getSize(newTags)) {
          const updates = editTags({
            channel: shopsChannel,
            newTags: newTags.slice(0, MAX_NUMBER_OF_TAGS[shopsChannel]),
            operation: OPERATIONS[shopsChannel].TAGS.CHANGE_TO,
            product,
          });

          product = updates.product.set('operations',
            updates.product.get('operations').concat(List(updates.operations))
          );
        } else {
          product = product.set('operations',
            product
              .get('operations')
              .push(
                Map({
                  type: OPERATIONS[shopsChannel].TAGS.CHANGE_TO,
                  value: EMPTY.LIST,
                })
              )
          );
        }

        return setIsComplete(product);
      }

      return result.update(productShopId, updateProduct);
    }

    return shopIds.reduce(reduceShopIds, productsByShopId);
  }

  function reduceChannelsForVariations(variations) {
    return function reduceChannels(productsByShopId, shopIds, shopsChannel) {
      function reduceShopIds(result, productShopId) {
        function updateProduct(product) {
          const updates = editVariations({
            channel: shopsChannel,
            product: product.set(FORM.VARIATIONS, newVariations({ channel: shopsChannel })),
            value: convert({ from: VELA, product, to: shopsChannel, type: VARIATIONS, value: variations }),
          });

          if (updates.product.hasIn([FORM.DETAILS, CATEGORY, 'oldCategory'])) {
            const category = updates.product.getIn([FORM.DETAILS, CATEGORY, 'oldCategory']);
            const detailsUpdates = editDetails({ channel, product, [CATEGORY]: { category }});
            updates.product = detailsUpdates.product.deleteIn([FORM.DETAILS, CATEGORY, 'oldCategory']);
          }

          return setIsComplete(
            updates.product.set('operations',
              updates.operations.reduce(
                replaceOperation,
                updates.product.get('operations', EMPTY.LIST)
              )
            )
          );
        }

        return result.update(productShopId, updateProduct);
      }

      return shopIds.reduce(reduceShopIds, productsByShopId);
    };
  }

  if (fromVelaForm) {
    switch (type) {
      case PROFILE.TAGS: {
        const products = state
          .get('channels')
          .reduce(reduceChannelsForTags, state.get('products'));

        return {
          state: state
            .deleteIn([VELA, TAGS, 'currentProfile'])
            .set('products', products)
            .set('isComplete', areComplete(products)),
        };
      }

      case PROFILE.VARIATIONS: {
        let product = state.get(VELA, EMPTY.MAP);
        const variations = product.getIn([VARIATIONS, 'oldVariations']) || newVariations({ channel });

        product = product.set(VARIATIONS, variations);

        const products = state
          .get('channels')
          .reduce(reduceChannelsForVariations(variations), state.get('products'));

        return {
          state: state
            .set(VELA, product)
            .set('products', products)
            .set('isComplete', areComplete(products)),
        };
      }

      default: {
        return { state };
      }
    }
  }

  let product = state.getIn(['products', shopId]);

  switch (type) {
    case PROFILE.TAGS: {
      const freeSpace = MAX_NUMBER_OF_TAGS[channel] - getSize(product.getIn([TAGS, VALUE]));
      const newTags = freeSpace > 0
        ? product.getIn([TAGS, NEW_TAGS], EMPTY.ORDERED_MAP).slice(0, freeSpace)
        : EMPTY.ORDERED_MAP;

      const updates = editTags({
        channel,
        newTags,
        operation: OPERATIONS[channel].TAGS.CHANGE_TO,
        product: product.deleteIn([TAGS, 'currentProfile']),
      });

      product = updates.product.set('operations',
        product
          .get('operations', EMPTY.LIST)
          .filter(filterOperations(OPERATIONS[channel].TAGS.CHANGE_TO, false))
          .concat(List(updates.operations))
      );

      break;
    }

    case PROFILE.VARIATIONS: {
      const variations = product.getIn([VARIATIONS, 'oldVariations']);

      if (!variations) return { state };

      const operations = product
        .get('operations')
        .filter(filterOperations(OPERATIONS[channel].VARIATIONS, false))
        .concat(product.getIn([VARIATIONS, 'operations'], EMPTY.LIST));

      switch (channel) {
        case ETSY: {
          if (product.hasIn([FORM.DETAILS, CATEGORY, 'oldCategory'])) {
            const category = product.getIn([FORM.DETAILS, CATEGORY, 'oldCategory']);
            const updates = editDetails({ channel, product, [CATEGORY]: { category }});
            product = updates.product.deleteIn([FORM.DETAILS, CATEGORY, 'oldCategory']);
          }

          break;
        }

        default: {
          break;
        }
      }

      product = updateProductVariationsErrors({
        channel,
        product: product
          .set('operations', operations)
          .set(VARIATIONS, variations),
      });

      break;
    }

    default: {
      break;
    }
  }

  const products = state.get('products').set(shopId, setIsComplete(product));

  return {
    state: state
      .set('products', products)
      .set('isComplete', areComplete(products)),
  };
}
